import { useEffect, useMemo, useState } from "react";
import { BASE_URL } from "utils/constant";

const ORGANIZATION_LOGO_CACHE_NAME = "sitemaster-organization-logo-v1";
const ORGANIZATION_LOGO_CACHE_PATH = "/__sitemaster_cache__/organization-logo/";

const objectUrlCache = new Map();
const dataUrlCache = new Map();
const inFlightLogoRequests = new Map();

const canUseWindow = () => typeof window !== "undefined";

export const getAbsoluteImageUrl = (path) => {
  if (!path) return null;
  if (/^(https?:)?\/\//i.test(path) || /^data:/i.test(path) || /^blob:/i.test(path)) {
    return path;
  }

  const sanitizedBaseUrl = BASE_URL.replace(/\/$/, "");
  const sanitizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${sanitizedBaseUrl}${sanitizedPath}`;
};

export const getOrganizationLogoUrl = (organization) =>
  getAbsoluteImageUrl(organization?.logo_image_file || organization?.logo_image);

const getOrganizationLogoCacheKey = (organization) => {
  const organizationId =
    organization?.id ||
    organization?.organization_id ||
    organization?.uuid ||
    organization?.slug ||
    organization?.organization_name;

  return organizationId ? `organization:${organizationId}:logo` : null;
};

const getCacheRequest = (key) => {
  if (!canUseWindow()) return null;
  return new Request(
    `${window.location.origin}${ORGANIZATION_LOGO_CACHE_PATH}${encodeURIComponent(key)}`,
  );
};

const openLogoCache = async () => {
  if (!canUseWindow() || !("caches" in window)) return null;
  return caches.open(ORGANIZATION_LOGO_CACHE_NAME);
};

const clearMemoryLogo = (key) => {
  const objectUrl = objectUrlCache.get(key);
  if (objectUrl) URL.revokeObjectURL(objectUrl);
  objectUrlCache.delete(key);
  dataUrlCache.delete(key);
};

const readCachedLogoBlob = async (key) => {
  try {
    const cache = await openLogoCache();
    const request = getCacheRequest(key);
    if (!cache || !request) return null;

    const response = await cache.match(request);
    return response ? response.blob() : null;
  } catch (error) {
    return null;
  }
};

const writeCachedLogoBlob = async (key, blob) => {
  try {
    if (!key || !blob) return;
    const cache = await openLogoCache();
    const request = getCacheRequest(key);
    if (!cache || !request) return;

    clearMemoryLogo(key);
    await cache.put(
      request,
      new Response(blob, {
        headers: {
          "content-type": blob.type || "application/octet-stream",
          "x-sm-cached-at": String(Date.now()),
        },
      }),
    );
  } catch (error) {
    // Cache Storage is a best-effort layer; callers still get the live URL fallback.
  }
};

const fetchLogoBlob = async (logoUrl) => {
  const response = await fetch(logoUrl, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Failed to fetch organization logo: ${response.status}`);
  }

  const blob = await response.blob();
  if (!blob?.size) {
    throw new Error("Fetched organization logo is empty");
  }

  return blob;
};

const getLogoBlob = async (organization) => {
  const key = getOrganizationLogoCacheKey(organization);
  const logoUrl = getOrganizationLogoUrl(organization);

  if (!key && !logoUrl) {
    return { blob: null, key, fallbackUrl: null };
  }

  if (logoUrl && /^data:/i.test(logoUrl)) {
    return { blob: null, key, fallbackUrl: logoUrl };
  }

  const requestKey = `${key || "anonymous"}:${logoUrl || "cache-only"}`;
  if (inFlightLogoRequests.has(requestKey)) {
    return inFlightLogoRequests.get(requestKey);
  }

  const request = (async () => {
    let cachedBlob = key ? await readCachedLogoBlob(key) : null;

    if (logoUrl) {
      try {
        const freshBlob = await fetchLogoBlob(logoUrl);
        if (key) await writeCachedLogoBlob(key, freshBlob);
        cachedBlob = freshBlob;
      } catch (error) {
        // The signed URL may have expired; use the stored binary if available.
      }
    }

    return { blob: cachedBlob, key, fallbackUrl: logoUrl };
  })();

  inFlightLogoRequests.set(requestKey, request);

  try {
    return await request;
  } finally {
    inFlightLogoRequests.delete(requestKey);
  }
};

const getObjectUrlFromBlob = (key, blob) => {
  if (!blob) return null;
  if (key && objectUrlCache.has(key)) return objectUrlCache.get(key);

  const objectUrl = URL.createObjectURL(blob);
  if (key) objectUrlCache.set(key, objectUrl);
  return objectUrl;
};

const getDataUrlFromBlob = (key, blob) => {
  if (!blob) return Promise.resolve(null);
  if (key && dataUrlCache.has(key)) return Promise.resolve(dataUrlCache.get(key));

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      if (key && dataUrl) dataUrlCache.set(key, dataUrl);
      resolve(dataUrl);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const cacheOrganizationLogoBinary = async (organization) => {
  const { blob } = await getLogoBlob(organization);
  return blob;
};

export const resolveOrganizationLogoSrc = async (organization, output = "objectUrl") => {
  const { blob, key, fallbackUrl } = await getLogoBlob(organization);

  if (!blob) return fallbackUrl;
  if (output === "dataUrl") return getDataUrlFromBlob(key, blob);
  return getObjectUrlFromBlob(key, blob);
};

export const warmOrganizationLogoCache = (organizations = []) => {
  organizations
    .filter(Boolean)
    .forEach((organization) => {
      cacheOrganizationLogoBinary(organization).catch(() => {});
    });
};

export const useOrganizationLogo = (
  organization,
  { output = "objectUrl", fallbackWhileLoading = true } = {},
) => {
  const logoUrl = useMemo(() => getOrganizationLogoUrl(organization), [organization]);
  const hasLogo = Boolean(logoUrl);
  const [logoSrc, setLogoSrc] = useState(fallbackWhileLoading ? logoUrl : null);
  const [isLogoLoading, setIsLogoLoading] = useState(hasLogo);

  useEffect(() => {
    let isMounted = true;

    if (!hasLogo) {
      setLogoSrc(null);
      setIsLogoLoading(false);
      return () => {
        isMounted = false;
      };
    }

    setLogoSrc(fallbackWhileLoading ? logoUrl : null);
    setIsLogoLoading(true);

    resolveOrganizationLogoSrc(organization, output)
      .then((resolvedSrc) => {
        if (!isMounted) return;
        setLogoSrc(resolvedSrc || logoUrl);
      })
      .catch(() => {
        if (!isMounted) return;
        setLogoSrc(logoUrl);
      })
      .finally(() => {
        if (isMounted) setIsLogoLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [fallbackWhileLoading, hasLogo, logoUrl, organization, output]);

  return { logoSrc, isLogoLoading, logoUrl };
};
